/**
 * Formatter Utilities
 * Common formatting functions for display across the UI
 */

/**
 * Parse a date string to a local Date object.
 * Handles date-only strings (YYYY-MM-DD) as local dates to avoid timezone shift.
 * Use this instead of new Date(dateStr) for date-only strings.
 */
export function parseLocalDate(dateStr: string): Date {
  // Check if it's a date-only string (YYYY-MM-DD) without time component
  const dateOnly = dateStr.split("T")[0];
  const parts = dateOnly.split("-");

  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // months are 0-indexed
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  // Fallback to standard parsing for other formats
  return new Date(dateStr);
}

/**
 * Format an invoice billing_month ("2026-08-01") as the month it covers,
 * e.g. "August 2026". Families must always be able to tell WHICH month a
 * charge is for (2026-09 Bayaan "double charge" confusion).
 */
export function formatBillingMonth(billingMonth: string | null | undefined): string {
  if (!billingMonth) return "";
  const d = parseLocalDate(billingMonth);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;

  const today = new Date();
  const birthDate = parseLocalDate(dateOfBirth);

  if (isNaN(birthDate.getTime())) return null;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Capitalize first letter of a string
 */
export function capitalize(str: string | null | undefined): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * "1 student" / "2 students". The bare `${n} students` produced "1 students"
 * on the teacher and Hifdh dashboards (audit Q08).
 *
 * The default plural follows ordinary English rules rather than bolting on an
 * "s", because this domain's most common nouns are exactly the ones a naive
 * rule gets wrong: class, campus, family.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? pluralOf(singular))}`;
}

/** Ordinary English plural of a noun. */
function pluralOf(singular: string): string {
  const lower = singular.toLowerCase();
  if (/(s|x|z|ch|sh)$/.test(lower)) return `${singular}es`;
  if (/[^aeiou]y$/.test(lower)) return `${singular.slice(0, -1)}ies`;
  return `${singular}s`;
}

/**
 * A family's display name, without doubling the word.
 *
 * Families are commonly stored as "Rahman Family", so `${name} Family` read
 * as "Rahman Family Family" on the parent dashboard (audit Q08).
 */
export function familyDisplayName(name?: string | null): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "Your family";
  const lower = trimmed.toLowerCase();
  const alreadyEndsWithFamily = lower === "family" || lower.endsWith(" family");
  return alreadyEndsWithFamily ? trimmed : `${trimmed} Family`;
}

/**
 * Format currency with symbol
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string = "USD"
): string {
  if (amount === null || amount === undefined) return "$0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "$0.00";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(num);
}

/**
 * Format an integer number of CENTS as currency. Payment APIs report Stripe
 * amounts in cents (refund_amount, refunded_amount_cents); passing those to
 * formatCurrency directly shows a $125 refund as "$12,500.00".
 */
export function formatCentsAsCurrency(
  cents: number | string | null | undefined,
  currency: string = "USD"
): string {
  const num = typeof cents === "string" ? Number(cents) : cents;
  if (num === null || num === undefined || !Number.isFinite(num)) return formatCurrency(0, currency);
  return formatCurrency(num / 100, currency);
}

/**
 * Compact number for chart axes/labels: 1500 -> "1.5K", 2_300_000 -> "2.3M".
 * Shared by all chart components (previously duplicated in 5 files).
 */
export function formatCompactNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(0);
}

/**
 * Compact currency for chart axes/labels: 1500 -> "$1.5K", 2_300_000 -> "$2.3M".
 */
export function formatCompactCurrency(value: number): string {
  return `$${formatCompactNumber(value)}`;
}

/**
 * Format a discount value for display. Handles both percent and
 * fixed-dollar discount types and guards against the NaN-render bug
 * that bit us across 4 sites on PR #962:
 * `parseFloat(null).toFixed(2)` returns the string "NaN" which then
 * renders as "$NaN" or "NaN%" in the UI cell.
 *
 * Backend returns discount_value as a Decimal-as-string (per
 * feedback_api_strings.md). Use Number() coercion + isFinite guard,
 * with a "—" fallback for null / undefined / unparseable.
 *
 * @param type 'percent' | 'fixed' — the discount type
 * @param value The discount value (Decimal-as-string from the API)
 * @param fallback Text to render when value is missing/invalid (default "—")
 */
export function formatDiscountAmount(
  type: "percent" | "fixed" | string | null | undefined,
  value: number | string | null | undefined,
  fallback: string = "—"
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  if (type === "percent") {
    return Number.isInteger(num) ? `${num}%` : `${num.toFixed(1)}%`;
  }
  return `$${num.toFixed(2)}`;
}

/**
 * Format date for display.
 * Handles date-only strings (YYYY-MM-DD) as local dates to avoid timezone shift.
 */
export function formatDate(
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return "-";

  const dateObj = typeof date === "string" ? parseLocalDate(date) : date;
  if (isNaN(dateObj.getTime())) return "-";

  return dateObj.toLocaleDateString(
    "en-US",
    options || { month: "short", day: "numeric", year: "numeric" }
  );
}

/**
 * Get today's date as YYYY-MM-DD string in local timezone.
 * Use this instead of new Date().toISOString().split("T")[0] to avoid timezone issues.
 */
export function getTodayString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Convert a Date object to YYYY-MM-DD string in local timezone.
 * Use this instead of date.toISOString().split("T")[0] to avoid timezone issues.
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format a date-only string (YYYY-MM-DD) without timezone issues.
 * Use this for dates that don't have time components (birthdays, due dates, etc.)
 */
export function formatLocalDate(
  dateString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return "-";

  // Parse YYYY-MM-DD format directly to avoid timezone issues
  const parts = dateString.split("T")[0].split("-");
  if (parts.length !== 3) return dateString;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // months are 0-indexed
  const day = parseInt(parts[2], 10);

  const date = new Date(year, month, day);
  if (isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString(
    "en-US",
    options || { month: "short", day: "numeric", year: "numeric" }
  );
}

/**
 * Format date with time
 */
export function formatDateTime(
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return "-";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "-";

  return dateObj.toLocaleString(
    "en-US",
    options || {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  );
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "-";

  // Remove all non-digits
  const digits = phone.replace(/\D/g, "");

  // Format as (XXX) XXX-XXXX for 10 digit numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Return as-is if not 10 digits
  return phone;
}

/**
 * Format name (first + last)
 */
export function formatFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
}

/**
 * Truncate text with ellipsis
 */
export function truncate(
  text: string | null | undefined,
  maxLength: number
): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

/**
 * Format percentage
 */
export function formatPercent(
  value: number | null | undefined,
  decimals: number = 1
): string {
  if (value === null || value === undefined) return "-";
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format number with commas
 */
export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return "-";
  return new Intl.NumberFormat("en-US").format(num);
}

/**
 * Display value or placeholder
 */
export function displayValue(
  value: string | number | null | undefined,
  placeholder: string = "-"
): string {
  if (value === null || value === undefined || value === "") {
    return placeholder;
  }
  return String(value);
}

// Error handling utilities are now centralized in @/utils/errors.ts
// Re-export for backward compatibility
export {
  getApiErrorMessage,
  getApiErrorCode,
  getApiErrorStatus,
  isApiErrorWithStatus,
  isNotFoundError,
  isForbiddenError,
  isUnauthorizedError,
  isValidationError,
} from "./errors";

/**
 * Format a timestamp as relative time (e.g., "2 minutes ago", "yesterday")
 */
export function formatRelativeTime(
  date: string | Date | null | undefined
): string {
  if (!date) return "";

  const now = new Date();
  const past = typeof date === "string" ? new Date(date) : date;

  if (isNaN(past.getTime())) return "";

  const diffMs = now.getTime() - past.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return "just now";
  } else if (diffMin < 60) {
    return diffMin === 1 ? "1 minute ago" : `${diffMin} minutes ago`;
  } else if (diffHour < 24) {
    return diffHour === 1 ? "1 hour ago" : `${diffHour} hours ago`;
  } else if (diffDay === 1) {
    return "yesterday";
  } else if (diffDay < 7) {
    return `${diffDay} days ago`;
  } else if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7);
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  } else if (diffDay < 365) {
    const months = Math.floor(diffDay / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  } else {
    const years = Math.floor(diffDay / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  }
}
